import classNames from 'classnames';
import type { StaticImageData } from 'next/image';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import AppIcon from '@alltrails/denali/components/AppIcon';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Modal from '@alltrails/modal/components/Modal/Modal';
import AppStore from '../Footer/StoreBadges/AppStore';
import GooglePlay from '../Footer/StoreBadges/GooglePlay';
import styles from './AppQrCodeModal.module.scss';

type Props = {
  className?: string;
  isOpen: boolean;
  onRequestClose: () => void;
  qrCode: string | StaticImageData;
  subtitleDesktop?: ReactNode;
  subtitleMobile?: ReactNode;
  title: ReactNode;
};

const AppQrCodeModal = ({ className, isOpen, onRequestClose, qrCode, subtitleDesktop, subtitleMobile, title }: Props) => {
  const intl = useIntl();
  const isMobileSizedScreen = useIsMobileSizedScreen();
  const languageRegionCode = useLanguageRegionCode();

  const subtitle = isMobileSizedScreen
    ? subtitleMobile || <FormattedMessage defaultMessage="Available now for iOS or Android." />
    : subtitleDesktop || <FormattedMessage defaultMessage="Scan the QR code to download the app." />;

  return (
    <Modal isOpen={isOpen} onCloseRequest={onRequestClose}>
      <div className={classNames(styles.container, className)}>
        <Image
          alt={intl.formatMessage({
            defaultMessage: 'A QR code that links to the AllTrails mobile app store listing'
          })}
          className={styles.qrCode}
          height={240}
          src={qrCode}
          unoptimized
          width={240}
        />
        <AppIcon className={styles.appIcon} size={135} />
        <Heading as="h2" className={styles.title} size="400">
          {title}
        </Heading>
        <Typography color="secondary" component="p" variant="text200">
          {subtitle}
        </Typography>
        <div className={styles.mobileStoreButtons}>
          <AppStore height={40} intl={intl} languageRegionCode={languageRegionCode} />
          <GooglePlay height={40} intl={intl} languageRegionCode={languageRegionCode} />
        </div>
      </div>
    </Modal>
  );
};

export default AppQrCodeModal;
